import { Observable } from 'rxjs';
import { ApiService } from '../api.service';
import { MasterScheduleView } from '../../data/data.generated';
import { TableResult } from './table';
import { FilterSettings } from 'src/app/pages/projects/project-filters/filter-settings';
import { RoleAndName } from 'src/app/pages/projects/project-card/project-card.component';

export class MasterSchedule {
  constructor(private _api: ApiService) { }

  public GetMasterScheduleItems(search: string, productCodes: string): Observable<TableResult<MasterScheduleView>> {
    return this._api.Get<TableResult<MasterScheduleView>>('GetMasterScheduleItems', {
      Search: search,
      ProductCodes: productCodes,
    });
  }

  public FillMasterScheduleActivitiesFromTemplate(templateId: number, orderStatusItemId: number) {
    return this._api.Get<{}>('FillMasterScheduleActivitiesFromTemplate', {
      TemplateId: templateId,
      OrderStatusItemId: orderStatusItemId,
    });
  }

  public GetMasterScheduleActivities(projectNumber: string, page: number, pageSize: number, filterSettings: FilterSettings): Observable<any> {
    return this._api.Get<any[]>('GetMasterScheduleActivities', {
      ProjectNumber: projectNumber,
      Page: page,
      PageSize: pageSize,
      ActivityName: filterSettings.activity,
      Search: filterSettings.searchText,
      Product: filterSettings.product,
      Project: filterSettings.project,
      EndUser: filterSettings.endUser,
      Customer: filterSettings.customer,
      PoNumber: filterSettings.poNumber,
      SalesManager: filterSettings.salesManager,
      ProjectManager: filterSettings.projectManager,
      ProjectManager2: filterSettings.projectManager2,
      Planner: filterSettings.planner,
      Status: filterSettings.status,
      PoStartDate: filterSettings.poStartDate?.toLocaleString('en-US') ?? null,
      PoEndDate: filterSettings.poEndDate?.toLocaleString('en-US') ?? null,
      ShipStartDate: filterSettings.shipStartDate?.toLocaleString('en-US') ?? null,
      ShipEndDate: filterSettings.shipEndDate?.toLocaleString('en-US') ?? null,
    });
  }

  public UpdateMasterScheduleActivity(activity: any, comment: string = ''): Observable<any[]> {
    activity.ExpectedStartDate?.setHours(0, 0, 0, 0);
    activity.ActualStartDate?.setHours(0, 0, 0, 0);
    activity.ExpectedEndDate?.setHours(0, 0, 0, 0);
    activity.ActualEndDate?.setHours(0, 0, 0, 0);
    return this._api.Get<any[]>('UpdateMasterScheduleActivity', {
      ActivityId: activity.Id,
      ExpectedStartDate: activity.ExpectedStartDate?.toLocaleString('en-US'),
      ActualStartDate: activity.ActualStartDate?.toLocaleString('en-US'),
      ExpectedEndDate: activity.ExpectedEndDate?.toLocaleString('en-US'),
      ActualEndDate: activity.ActualEndDate?.toLocaleString('en-US'),
      IsConfirmed: activity.IsConfirmed ?? false,
      IsComplete: activity.IsComplete ?? false,
      IsLocked: activity.IsLocked ?? false,
      HasExpectedDatesOverride: activity.HasExpectedDatesOverride ?? false,
      Comment: comment,
    });
  }

  public UpdateMasterScheduleActivityBatch(activities: any[], comment: string = ''): Observable<any[]> {
    let activityData: any[] = []
    activities.forEach(activity => {
      activity.ExpectedStartDate?.setHours(0, 0, 0, 0);
      activity.ActualStartDate?.setHours(0, 0, 0, 0);
      activity.ExpectedEndDate?.setHours(0, 0, 0, 0);
      activity.ActualEndDate?.setHours(0, 0, 0, 0);
      activityData.push({
        ActivityId: activity.Id,
        ExpectedStartDate: activity.ExpectedStartDate?.toLocaleString('en-US'),
        ActualStartDate: activity.ActualStartDate?.toLocaleString('en-US'),
        ExpectedEndDate: activity.ExpectedEndDate?.toLocaleString('en-US'),
        ActualEndDate: activity.ActualEndDate?.toLocaleString('en-US'),
        IsConfirmed: activity.IsConfirmed ?? false,
        IsComplete: activity.IsComplete ?? false,
        IsLocked: activity.IsLocked ?? false,
        HasExpectedDatesOverride: activity.HasExpectedDatesOverride ?? false,
        Comment: comment,
      });
    });
    return this._api.Get<any[]>('UpdateMasterScheduleActivityBatch', {
      Activities: JSON.stringify(activityData),
    });
  }

  public GetMasterScheduleActivityRevisions(activityId: number): Observable<any[]> {
    return this._api.Get<any[]>('GetMasterScheduleActivityRevisions', {
      ActivityId: activityId,
    });
  }

  public GetMasterScheduleActivityNotes(activityId: number = 0, projectId: number = 0): Observable<any[]> {
    return this._api.Get<any[]>('GetMasterScheduleActivityNotes', {
      ActivityId: activityId,
      ProjectId: projectId,
    });
  }

  public InsertMasterScheduleActivityNote(activityId: number, note: string, duplicateToAllItems: boolean = false): Observable<number> {
    return this._api.Get<any>('InsertMasterScheduleActivityNote', {
      ActivityId: activityId,
      Note: note,
      DuplicateToAllItems: duplicateToAllItems,
    });
  }

  public InsertMasterScheduleProjectNote(projectId: number, note: string): Observable<number> {
    return this._api.Get<any>('InsertMasterScheduleProjectNote', {
      ProjectId: projectId,
      Note: note,
    });
  }

  public GetMasterScheduleHolidays(): Observable<any[]> {
    return this._api.Get<any[]>('GetMasterScheduleHolidays', {});
  }

  public GetMasterScheduleProjects(filterSettings: FilterSettings, sortToTopProject: string | null = null
  ): Observable<any[]> {
    return this._api.Get<any[]>('GetMasterScheduleProjects',
      {
        Search: filterSettings.searchText,
        Product: filterSettings.product,
        Project: filterSettings.project,
        EndUser: filterSettings.endUser,
        Customer: filterSettings.customer,
        PoNumber: filterSettings.poNumber,
        SalesManager: filterSettings.salesManager,
        TechnicalEngineer: filterSettings.technicalEngineer,
        ProjectManager: filterSettings.projectManager,
        ProjectManager2: filterSettings.projectManager2,
        Planner: filterSettings.planner,
        MechanicalEngineer: filterSettings.mechanicalEngineer,
        MechanicalDrafter: filterSettings.mechanicalDrafter,
        ElectricalEngineer: filterSettings.electricalEngineer,
        ElectricalDrafter: filterSettings.electricalDrafter,
        Status: filterSettings.status,
        PoStartDate: filterSettings.poStartDate?.toLocaleString('en-US') ?? null,
        PoEndDate: filterSettings.poEndDate?.toLocaleString('en-US') ?? null,
        ShipStartDate: filterSettings.shipStartDate?.toLocaleString('en-US') ?? null,
        ShipEndDate: filterSettings.shipEndDate?.toLocaleString('en-US') ?? null,
        SortToTopProject: sortToTopProject,
      });
  }

  public GetMasterScheduleProject(projectNumber: string): Observable<any> {
    return this._api.Get<any>('GetMasterScheduleProject', {
      ProjectNumber: projectNumber,
    });
  }

  public GetMasterScheduleFilterList(type: string, search: string, maxResults: number = 20): Observable<string[]> {
    return this._api.Get<string[]>('GetMasterScheduleFilterList', {
      Type: type,
      Search: search,
      MaxResults: maxResults,
    });
  }

  public UpdateMasterScheduleProjectNames(projectId: number, standardNames: RoleAndName[], customNames: RoleAndName[]) {
    return this._api.Get<{}>('UpdateMasterScheduleProjectNames', {
      ProjectId: projectId,
      SalesManager: standardNames.find(name => name.Property === 'SalesManager')?.Name ?? '',
      TechnicalEngineer: standardNames.find(name => name.Property === 'TechnicalEngineer')?.Name ?? '',
      ProjectManager: standardNames.find(name => name.Property === 'ProjectManager')?.Name ?? '',
      ProjectManager2: standardNames.find(name => name.Property === 'ProjectManager2')?.Name ?? '',
      Planner: standardNames.find(name => name.Property === 'Planner')?.Name ?? '',
      MechanicalEngineer: standardNames.find(name => name.Property === 'MechanicalEngineer')?.Name ?? '',
      MechanicalDrafter: standardNames.find(name => name.Property === 'MechanicalDrafter')?.Name ?? '',
      ElectricalEngineer: standardNames.find(name => name.Property === 'ElectricalEngineer')?.Name ?? '',
      ElectricalDrafter: standardNames.find(name => name.Property === 'ElectricalDrafter')?.Name ?? '',
      CustomNames: customNames.map(name => `${name.Role};${name.Name}`).join('|'),
    });
  }

  public DuplicateMasterScheduleActivityDates(fromActivityId: number, toProjectId: number): Observable<any> {
    return this._api.Get<any[]>('DuplicateMasterScheduleActivityDates', {
      FromActivityId: fromActivityId,
      ToProjectId: toProjectId,
    });
  }

  public DuplicateMasterScheduleProjectNames(fromProjectId: number, toProjectId: number): Observable<any> {
    return this._api.Get<any[]>('DuplicateMasterScheduleProjectNames', {
      FromProjectId: fromProjectId,
      ToProjectId: toProjectId,
    });
  }

  public ToggleMasterScheduleProjectIsHot(projectId: number, isHot: boolean): Observable<any> {
    return this._api.Get<any>('ToggleMasterScheduleProjectIsHot', {
      ProjectId: projectId,
      IsHot: isHot,
    });
  }

  public BuildMasterScheduleTemplate(projectId: number, templateName: string): Observable<any> {
    return this._api.Get<any>('BuildMasterScheduleTemplate', {
      ProjectId: projectId,
      TemplateName: templateName,
    });
  }

  public RebuildMasterScheduleTemplate(projectId: number, templateName: string): Observable<any> {
    return this._api.Get<any>('RebuildMasterScheduleTemplate', {
      ProjectId: projectId,
      TemplateName: templateName,
    });
  }
}